import * as Yup from 'yup';
import { format, startOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { PATH_DASHBOARD } from '@/routes/paths';

import { formatCnpjInput, isValidCnpj, sanitizeCnpj } from '@/utils/cnpj';
import { validateCPF } from '@/utils/validate';
import { CODIGO_TRIBUTACAO_MUNICIPAL, FISCAL_EDIT_CERTIFICATE_SECTION_ID } from '../constants';

export const TIPO_DOCUMENTO = { cnpj: 'cnpj', cpf: 'cpf' };

// Na URL, e não em state de navegação: a correção sobrevive a recarregar a página.
export const CORRIGIR_PARAM = 'corrigir';

export const caminhoParaCorrigir = (notaId) =>
  `${PATH_DASHBOARD.fiscal.financialInvoicesAvulsa}?${CORRIGIR_PARAM}=${encodeURIComponent(
    notaId
  )}`;

// O wizard lê os dois: abre no passo que falta e, ao concluir, volta para a avulsa.
export const PASSO_PARAM = 'passo';
export const VOLTAR_PARAM = 'voltar';
export const VOLTAR_PARA_AVULSA = 'avulsa';

/**
 * Para onde leva o botão do passo que falta.
 *
 * Quem já tem o cadastro de vendas salvo corrige pela edição (certificado vencido, nota de teste
 * recusada): o wizard é só de cadastro novo e recusa quem já tem dados fiscais.
 */
// A seção da edição que resolve cada passo, quando ela tem âncora. Sem âncora, a edição abre
// no topo.
const ANCORA_NA_EDICAO = {
  certificado: FISCAL_EDIT_CERTIFICATE_SECTION_ID,
};

export const destinoDoPasso = (passo, { configurado }) => {
  if (configurado) {
    const ancora = ANCORA_NA_EDICAO[passo?.chave];
    return `${PATH_DASHBOARD.fiscal.financialInvoicesEdit}${ancora ? `#${ancora}` : ''}`;
  }

  // Sem chave (uma recusa de cadastro que não diz o passo), o wizard abre no começo.
  const params = new URLSearchParams(passo?.chave ? { [PASSO_PARAM]: passo.chave } : {});
  params.set(VOLTAR_PARAM, VOLTAR_PARA_AVULSA);

  return `${PATH_DASHBOARD.fiscal.financialInvoicesRegister}?${params.toString()}`;
};

export const SERVICO_OUTRO = 'outro';

const onlyDigits = (value = '') => String(value ?? '').replace(/\D/g, '');

export const formatCpfInput = (value = '') =>
  onlyDigits(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');

export const formatDocumento = (tipo, value) =>
  tipo === TIPO_DOCUMENTO.cpf ? formatCpfInput(value) : formatCnpjInput(value);

export const sanitizeDocumento = (tipo, value) =>
  tipo === TIPO_DOCUMENTO.cpf ? onlyDigits(value) : sanitizeCnpj(value);

/**
 * O que está errado no documento, em texto para quem não sabe o que é dígito verificador.
 * `null` quando ele está certo.
 *
 * O CNPJ fala em caracteres, e não em números: desde 2026 ele pode ter letras.
 */
export const problemaNoDocumento = (tipo, value) => {
  const documento = sanitizeDocumento(tipo, value);

  if (!documento) return 'Informe o CPF ou CNPJ de quem contratou o serviço.';

  if (tipo === TIPO_DOCUMENTO.cpf) {
    if (documento.length < 11) return 'CPF incompleto. Confira os 11 números antes de continuar.';
    return validateCPF(documento) ? null : 'Esse CPF não existe. Confira os números.';
  }

  if (documento.length < 14) {
    return 'CNPJ incompleto. Confira os 14 caracteres antes de continuar.';
  }

  return isValidCnpj(documento) ? null : 'Esse CNPJ não existe. Confira os caracteres.';
};

/** "3.500,00" a partir do que foi digitado, como numa maquininha: os dois últimos são centavos. */
export const formatValorInput = (value = '') => {
  const digits = onlyDigits(value).replace(/^0+/, '').slice(0, 12);
  if (!digits) return '';

  const cents = digits.padStart(3, '0');
  const reais = cents.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `${reais},${cents.slice(-2)}`;
};

/** "3.500,00" → "3500.00", o formato do motor. Vazio vira `null`. */
export const valorParaApi = (value = '') => {
  const digits = onlyDigits(value);
  if (!digits) return null;

  const cents = digits.padStart(3, '0');

  return `${Number(cents.slice(0, -2))}.${cents.slice(-2)}`;
};

/** "3500.00" (ou número) → "3.500,00". */
export const valorDaApi = (value) => {
  if (value == null || value === '') return '';

  return formatValorInput(Number(value).toFixed(2));
};

export const formatMoeda = (value) =>
  value == null || value === ''
    ? '—'
    : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

export const rotuloDoMes = (competencia) => {
  const [ano, mes] = String(competencia || '')
    .split('-')
    .map(Number);
  if (!ano || !mes) return '—';

  return capitalize(format(new Date(ano, mes - 1, 1), "MMMM 'de' yyyy", { locale: ptBR }));
};

/**
 * O mês corrente e os 12 anteriores. Uma nota de um mês fora da lista (a correção de uma nota
 * antiga) entra também, para o campo não aparecer vazio.
 */
export const opcoesDeMes = (today = new Date(), incluir = null) => {
  const meses = Array.from({ length: 13 }, (_, index) =>
    format(startOfMonth(subMonths(today, index)), 'yyyy-MM')
  );

  if (incluir && !meses.includes(incluir)) meses.push(incluir);

  return meses.map((value) => ({ value, label: rotuloDoMes(value) }));
};

export const mesAtual = (today = new Date()) => format(today, 'yyyy-MM');

export const SERVICO_PADRAO = 'padrao';

const mesmoServico = (servico, ctn, nbs, municipal) =>
  servico.codigoTributacao === ctn &&
  servico.codigoNbs === nbs &&
  servico.codigoTributacaoMunicipal === (municipal || '');

/**
 * As opções de tipo de serviço: primeiro o `servico_padrao` da situação (o do cadastro de vendas
 * ou o da última avulsa), depois os outros serviços do cadastro de vendas, para a troca.
 *
 * O desdobro municipal separa serviços: em Recife, o mesmo código nacional é 501 presencial e 502
 * a distância, e juntar os dois numa opção emitiria um com o código do outro.
 */
export const opcoesDeServico = (servicoPadrao, fiscalData) => {
  const opcoes = [];
  const jaTem = (ctn, nbs, municipal) =>
    opcoes.some((opcao) => mesmoServico(opcao, ctn, nbs, municipal));

  if (servicoPadrao?.codigo_tributacao_nacional && servicoPadrao?.codigo_nbs) {
    opcoes.push({
      value: SERVICO_PADRAO,
      label: servicoPadrao.descricao || `Serviço ${servicoPadrao.codigo_tributacao_nacional}`,
      codigoTributacao: servicoPadrao.codigo_tributacao_nacional,
      codigoNbs: servicoPadrao.codigo_nbs,
      codigoTributacaoMunicipal: servicoPadrao.codigo_tributacao_municipal || '',
      descricao: servicoPadrao.descricao || '',
    });
  }

  (fiscalData?.servicos ?? []).forEach((servico, index) => {
    const ctn = servico?.codigo_tributacao_nacional_iss;
    const nbs = servico?.codigo_nbs;
    const municipal = servico?.codigo_tributacao_municipal;
    if (!ctn || !nbs || jaTem(ctn, nbs, municipal)) return;

    opcoes.push({
      value: String(index),
      label: servico.discriminacao || `Serviço ${ctn}`,
      codigoTributacao: ctn,
      codigoNbs: nbs,
      codigoTributacaoMunicipal: municipal || '',
      descricao: '',
    });
  });

  return opcoes;
};

/** O formulário vazio, com o serviço padrão já escolhido e a descrição dele para editar. */
export const valoresIniciais = ({ servicos = [], today = new Date() } = {}) => {
  const [padrao] = servicos;

  return {
    tipoDocumento: TIPO_DOCUMENTO.cnpj,
    documento: '',
    razaoSocial: '',
    email: '',
    descricao: padrao?.descricao ?? '',
    valor: '',
    competencia: mesAtual(today),
    servicoOpcao: padrao ? padrao.value : SERVICO_OUTRO,
    codigoTributacao: padrao?.codigoTributacao ?? '',
    codigoNbs: padrao?.codigoNbs ?? '',
    codigoTributacaoMunicipal: padrao?.codigoTributacaoMunicipal ?? '',
  };
};

// Sem opção igual, inclusive no desdobro, a nota abre em "Outro": uma opção escolhida esconde os
// códigos, e o desdobro da nota sairia sem o produtor ver que ele difere do serviço da opção.
const opcaoDoServico = (servicos, codigoTributacao, codigoNbs, municipal) =>
  servicos.find((servico) => mesmoServico(servico, codigoTributacao, codigoNbs, municipal))
    ?.value ?? SERVICO_OUTRO;

/**
 * Os campos do bloco do tomador a partir de um tomador de nota anterior. O endereço que a lista
 * de tomadores pode trazer fica de fora: o motor não recebe endereço do tomador.
 */
export const valoresDoTomador = (tomador) => {
  const tipo =
    tomador?.tipo_documento === TIPO_DOCUMENTO.cpf ? TIPO_DOCUMENTO.cpf : TIPO_DOCUMENTO.cnpj;

  return {
    tipoDocumento: tipo,
    documento: formatDocumento(tipo, tomador?.documento),
    razaoSocial: tomador?.razao_social ?? '',
    email: tomador?.email ?? '',
  };
};

/** O formulário inteiro a partir de uma nota (o "Corrigir e emitir de novo"). */
export const valoresDaNota = (nota, servicos = []) => {
  const codigoTributacao = nota?.servico?.codigo_tributacao_nacional ?? '';
  const codigoNbs = nota?.servico?.codigo_nbs ?? '';
  const codigoTributacaoMunicipal = nota?.servico?.codigo_tributacao_municipal || '';

  return {
    ...valoresIniciais({ servicos }),
    ...valoresDoTomador(nota?.tomador),
    descricao: nota?.servico?.descricao ?? '',
    valor: valorDaApi(nota?.valor_servico),
    competencia: nota?.data_competencia ? String(nota.data_competencia).slice(0, 7) : mesAtual(),
    servicoOpcao: opcaoDoServico(servicos, codigoTributacao, codigoNbs, codigoTributacaoMunicipal),
    codigoTributacao,
    codigoNbs,
    codigoTributacaoMunicipal,
  };
};

// Vazio, a chave não sai, nem `""` nem `null`: a Focus recusa os dois, e ausente é "a prefeitura
// não exige", o caso da maioria.
const desdobroMunicipal = (values) => {
  const codigo = onlyDigits(values.codigoTributacaoMunicipal);
  return codigo ? { codigo_tributacao_municipal: codigo } : {};
};

export const montarPayload = (values, { emissorId }) => ({
  emissor_id: emissorId,
  tomador: {
    tipo_documento: values.tipoDocumento,
    documento: sanitizeDocumento(values.tipoDocumento, values.documento),
    razao_social: values.razaoSocial.trim(),
    email: values.email.trim(),
  },
  servico: {
    descricao: values.descricao.trim(),
    codigo_tributacao_nacional: onlyDigits(values.codigoTributacao),
    codigo_nbs: onlyDigits(values.codigoNbs),
    ...desdobroMunicipal(values),
  },
  valor_servico: valorParaApi(values.valor),
  data_competencia: `${values.competencia}-01`,
});

/**
 * Do caminho do erro do motor (`tomador.documento`) para o campo da tela. Caminho desconhecido
 * fica de fora: o `detail` mostra a mensagem.
 */
const CAMPO_DO_CAMINHO = {
  'tomador.tipo_documento': 'tipoDocumento',
  'tomador.documento': 'documento',
  'tomador.razao_social': 'razaoSocial',
  'tomador.email': 'email',
  'servico.descricao': 'descricao',
  'servico.codigo_tributacao_nacional': 'codigoTributacao',
  'servico.codigo_nbs': 'codigoNbs',
  'servico.codigo_tributacao_municipal': 'codigoTributacaoMunicipal',
  valor_servico: 'valor',
  data_competencia: 'competencia',
};

export const campoDoCaminho = (caminho) => CAMPO_DO_CAMINHO[caminho] ?? null;

/**
 * Do `campo` da recusa traduzida (T4) para o campo da tela que recebe o foco no "Corrigir e
 * emitir de novo". O endereço não está aqui: o formulário não tem onde corrigi-lo, e essa recusa
 * vira orientação (ver `RecusaNotaAvulsaDialog`).
 */
const CAMPO_DA_RECUSA = {
  tomador_documento: 'documento',
  tomador_tipo_documento: 'documento',
  tomador_razao_social: 'razaoSocial',
  tomador_email: 'email',
  servico_descricao: 'descricao',
  servico_codigo_tributacao_nacional: 'codigoTributacao',
  servico_codigo_nbs: 'codigoNbs',
  servico_codigo_tributacao_municipal: 'codigoTributacaoMunicipal',
  valor_servico: 'valor',
  data_competencia: 'competencia',
};

export const campoDaRecusa = (campo) => CAMPO_DA_RECUSA[campo] ?? null;

const CAMPOS_DO_SERVICO_AVANCADO = ['codigoTributacao', 'codigoNbs', 'codigoTributacaoMunicipal'];

export const isCampoDoServicoAvancado = (campo) => CAMPOS_DO_SERVICO_AVANCADO.includes(campo);

export const notaAvulsaSchema = () =>
  Yup.object({
    tipoDocumento: Yup.string().oneOf(Object.values(TIPO_DOCUMENTO)),
    documento: Yup.string().test('documento', function validar(value) {
      const problema = problemaNoDocumento(this.parent.tipoDocumento, value);
      return problema ? this.createError({ message: problema }) : true;
    }),
    razaoSocial: Yup.string()
      .trim()
      .required('Informe o nome de quem contratou o serviço.')
      .max(255, 'O nome tem no máximo 255 caracteres.'),
    email: Yup.string().trim().email('Esse e-mail não parece certo. Confira o endereço.'),
    descricao: Yup.string()
      .trim()
      .required('Descreva o serviço como ele deve sair na nota.')
      .max(2000, 'A descrição tem no máximo 2.000 caracteres.'),
    valor: Yup.string().test(
      'valor',
      'Informe o valor do serviço.',
      (value) => Number(valorParaApi(value)) > 0
    ),
    competencia: Yup.string().required('Escolha o mês em que o serviço foi prestado.'),
    servicoOpcao: Yup.string(),
    codigoTributacao: Yup.string().test('ctn', 'O código de tributação tem 6 números.', (value) =>
      /^\d{6}$/.test(onlyDigits(value))
    ),
    codigoNbs: Yup.string().test('nbs', 'O código NBS tem 9 números.', (value) =>
      /^\d{9}$/.test(onlyDigits(value))
    ),
    // Opcional, porque só algumas prefeituras desdobram o código; preenchido, o errado também é
    // recusado.
    codigoTributacaoMunicipal: Yup.string()
      .trim()
      .matches(/^[0-9]{3}$/, {
        message: CODIGO_TRIBUTACAO_MUNICIPAL.invalid,
        excludeEmptyString: true,
      }),
  });
